import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorDetail } from "@/lib/api";

// A silent frame, played on the click itself, so the browser lets MiLu speak later.
const SILENCE = "data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA";

// One audio player for the whole page: fetch the words, read them out, keep a queue, stop when asked.
export const useSpeaker = (voice, speed = 0) => {
  const [playing, setPlaying] = useState("");
  const [loading, setLoading] = useState("");
  const [paused, setPaused] = useState(false);
  const [said, setSaid] = useState(null);   // what is being read right now, for reading along
  const [queued, setQueued] = useState([]); // what is still waiting its turn
  const player = useRef(null);
  const line = useRef([]);
  const done = useRef(null);
  const running = useRef(false);  // true while a bulletin is playing itself out
  const walls = useRef(0);        // stories a publisher would not let MiLu open
  const era = useRef(0);          // bumped on every new ask, so an old fetch never speaks over it

  useEffect(() => () => player.current?.pause(), []);

  // Made on the click, so playback is never blocked by the browser's autoplay rules.
  const readyPlayer = () => {
    if (!player.current) {
      player.current = new Audio(SILENCE);
      player.current.play().catch(() => {});
    }
    return player.current;
  };

  const hush = () => {
    player.current?.pause();
    setPlaying("");
    setPaused(false);
  };

  // hold it there, then carry on from the same word
  const togglePause = () => {
    const audio = player.current;
    if (!audio || !playing) return;
    if (audio.paused) {
      audio.play().catch(() => {});
      setPaused(false);
    } else {
      audio.pause();
      setPaused(true);
    }
  };

  // let the sound slip away rather than cut it off
  const fadeOut = (over = 8) => {
    const audio = player.current;
    if (!audio) {
      stop();
      return;
    }
    const step = 100;
    const drop = Math.max(0.01, audio.volume / Math.max(1, (over * 1000) / step));
    const tick = setInterval(() => {
      const a = player.current;
      if (!a) {
        clearInterval(tick);
        return;
      }
      a.volume = Math.max(0, a.volume - drop);
      if (a.volume <= 0.02) {
        clearInterval(tick);
        a.volume = 1;
        stop();
      }
    }, step);
  };

  const stop = () => {
    era.current += 1;
    hush();
    player.current = null;
    line.current = [];
    setQueued([]);
    setSaid(null);
    running.current = false;
    walls.current = 0;
  };

  const advance = async () => {
    const next = line.current.shift();
    setQueued([...line.current]);
    if (next) return speak(next);
    player.current = null;
    setSaid(null);
    // the ones no publisher would open, mentioned once at the end instead of interrupting
    if (walls.current) {
      const n = walls.current;
      toast.info(n === 1
        ? "One story could not be opened — its publisher blocked MiLu"
        : `${n} stories could not be opened — their publishers blocked MiLu`);
      walls.current = 0;
    }
    running.current = false;
    done.current?.();
    done.current = null;
    return null;
  };

  const speak = async (item) => {
    const mine = era.current;
    const audio = readyPlayer();
    setLoading(item.key);
    setSaid({ key: item.key, label: item.label || "", text: "" });
    // let the browser paint "Getting it…" before we go off and fetch the words,
    // so the button always answers the click even when the fetch fails at once
    await new Promise((r) => setTimeout(r, 0));
    try {
      const text = await item.getText();
      const res = await api.post("/home/say", { text, voice: item.voice || voice, speed },
                                { responseType: "blob" });
      if (mine !== era.current) return null;  // the reader has asked for something else since
      audio.src = URL.createObjectURL(res.data);
      audio.onended = () => { setPlaying(""); advance(); };
      setSaid({ key: item.key, label: item.label || "", text });
      setPlaying(item.key);
      setPaused(false);
      await audio.play();
      // get the next one's words ready while this one plays, so the listen runs on
      line.current[0]?.getText().catch(() => {});
      return text;
    } catch (e) {
      if (mine !== era.current) return null;
      setPlaying("");
      setSaid(null);
      setLoading("");
      if (running.current) {  // in a bulletin: walk quietly past a story we cannot open
        walls.current += 1;
        return advance();
      }
      line.current = [];
      setQueued([]);
      toast.error((e.response ? errorDetail(e) : e.message) || "MiLu could not read that out");
      return null;
    } finally {
      if (mine === era.current) setLoading("");
    }
  };

  const play = async (key, getText, label = "", voiceOverride = "") => {
    const wasPlaying = playing === key || loading === key;
    if (wasPlaying) {
      stop();
      return null;
    }
    era.current += 1;
    hush();
    line.current = [];
    running.current = false;
    walls.current = 0;
    setQueued([]);
    return speak({ key, getText, label, voice: voiceOverride });
  };

  // several stories, one after another
  const playQueue = async (items, onDone = null) => {
    if (!items.length) return null;
    era.current += 1;
    hush();
    done.current = onDone;
    line.current = items.slice(1);
    running.current = true;
    walls.current = 0;
    setQueued(line.current);
    return speak(items[0]);
  };

  const skip = () => {
    hush();
    return advance();
  };

  return { playing, loading, paused, said, queued, play, playQueue, skip, stop, togglePause, fadeOut };
};
